import PropTypes from 'prop-types';
import React, {useEffect, useState, useRef} from 'react';
import {connect} from 'react-redux';

const ANIMATIONS = [
    'announcement-float',
    'announcement-wave',
    'announcement-pulse-drift',
    'announcement-bounce-around',
    'announcement-swing',
];

const COLORS = [
    '#e74c3c',
    '#3498db',
    '#2ecc71',
    '#9b59b6',
    '#f39c12',
    '#1abc9c',
    '#e67e22',
];

const APPEAR_DURATION = 800;
const MOVE_DURATION = 700;
const PHASE_DURATION = APPEAR_DURATION + MOVE_DURATION;

const isUserAllowed = (userId, allowedUserIds) => {
    if (!userId) {
        return false;
    }

    if (!Array.isArray(allowedUserIds) || 0 === allowedUserIds.length) {
        return false;
    }

    return allowedUserIds.includes(String(userId));
};

const getRandomPosition = () => ({
    top: `${5 + Math.random() * 70}%`,
    left: `${5 + Math.random() * 65}%`,
});

export const AnimatedAnnouncement = ({userId, allowedUserIds, phrases}) => {
    const [visiblePhrases, setVisiblePhrases] = useState([]);
    const indexRef = useRef(0);
    const timerRef = useRef(null);
    const innerTimerRef = useRef(null);

    useEffect(() => {
        if (!isUserAllowed(userId, allowedUserIds)) {
            return;
        }

        if (!Array.isArray(phrases) || 0 === phrases.length) {
            return;
        }

        const addPhrase = () => {
            const idx = indexRef.current;

            if (idx >= phrases.length) {
                return;
            }

            const position = getRandomPosition();
            const animation = ANIMATIONS[idx % ANIMATIONS.length];
            const color = COLORS[idx % COLORS.length];

            setVisiblePhrases((prev) => [
                ...prev,
                {text: phrases[idx], position, animation, color, phase: 'appearing'},
            ]);

            innerTimerRef.current = setTimeout(() => {
                setVisiblePhrases((prev) =>
                    prev.map((item, i) =>
                        i === idx ? {...item, phase: 'moving'} : item
                    )
                );
            }, APPEAR_DURATION);

            indexRef.current = idx + 1;

            if (idx + 1 < phrases.length) {
                timerRef.current = setTimeout(addPhrase, PHASE_DURATION);
            }
        };

        addPhrase();

        return () => {
            clearTimeout(timerRef.current);
            clearTimeout(innerTimerRef.current);
        };
    }, [userId, allowedUserIds, phrases]);

    if (!isUserAllowed(userId, allowedUserIds)) {
        return null;
    }

    if (!Array.isArray(phrases) || 0 === phrases.length) {
        return null;
    }

    return (
        <div className='animated-announcement'>
            {visiblePhrases.map((item, index) => (
                <div
                    key={index}
                    className={`animated-announcement__phrase animated-announcement__phrase--${item.phase}`}
                    style={{
                        ...item.position,
                        color: item.color,
                        animationName: 'moving' === item.phase ? `${item.animation}, announcement-color-shift` : undefined,
                    }}
                >
                    {item.text}
                </div>
            ))}
        </div>
    );
};

AnimatedAnnouncement.propTypes = {
    allowedUserIds: PropTypes.arrayOf(PropTypes.string),
    phrases: PropTypes.arrayOf(PropTypes.string),
    userId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
};

const parseEnvList = (value) => {
    if (!value || 'string' !== typeof value) {
        return [];
    }

    return value.split(',').map((item) => item.trim()).filter(Boolean);
};

AnimatedAnnouncement.defaultProps = {
    allowedUserIds: parseEnvList(process.env.REACT_APP_ANNOUNCEMENT_USER_IDS),
    phrases: parseEnvList(process.env.REACT_APP_ANNOUNCEMENT_PHRASES),
    userId: null,
};

const mapStateToProps = (state) => ({
    userId: state.app.security.user ? state.app.security.user.id : null,
});

export default connect(mapStateToProps)(AnimatedAnnouncement);
